/**
 * impostor — OCTAHEDRAL IMPOSTORS for the far foliage ring (perf frontier
 * 2026-09-08, item 5). A tree beyond the near ring is one billboard quad whose
 * texture is the tree itself rendered from the view direction: at load the
 * species' LOD1 mesh is baked once from an N×N grid of directions over the
 * upper hemisphere (hemi-octahedral mapping: the hemisphere unfolds onto a
 * square, every grid cell a direction) into one atlas; at draw time the
 * fragment picks the grid cell nearest its own view direction. ~15k
 * triangles per tree become 2, the atlas is shared per species, and the
 * quads are instanced per species — the technique of Amplify Impostors /
 * the three.js forest showcase, in its simplest (nearest-frame) form.
 *
 * Pure parts (harness): the octahedral encode/decode and the frame grid.
 * Baking needs a renderer (three's WebGPURenderer, WebGL2 fallback too).
 */
import * as THREE from 'three/webgpu';
import { texture as tslTexture, uv, vec2, vec3, float, positionWorld, cameraPosition, normalize, floor, clamp, abs, sign, select, Fn } from 'three/tsl';
import { makeAnchorGroup, onRenderAnchor, toAnchorLocal } from './local_origin.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

/** Frames per atlas side (N×N views). */
export const IMPOSTOR_FRAMES = 8;
/** Atlas edge (px); each frame is ATLAS / FRAMES square. */
export const IMPOSTOR_ATLAS_PX = 1024;

/** Hemi-octahedral encode: a unit direction with y ≥ 0 → [0,1]² (Lagarde/Sousa). */
export function hemiOctEncode(x: number, y: number, z: number): [number, number] {
  const l1 = Math.abs(x) + Math.abs(y) + Math.abs(z) || 1;
  const px = x / l1, pz = z / l1; // y ≥ 0 hemisphere: (px, pz) with |px| + |pz| ≤ 1
  // Rotate the diamond into the square.
  const u = (px + pz) * 0.5 + 0.5;
  const v = (pz - px) * 0.5 + 0.5;
  return [Math.min(1, Math.max(0, u)), Math.min(1, Math.max(0, v))];
}

/** Hemi-octahedral decode: [0,1]² → unit direction with y ≥ 0. */
export function hemiOctDecode(u: number, v: number): [number, number, number] {
  const a = u * 2 - 1, b = v * 2 - 1;
  const px = (a - b) * 0.5, pz = (a + b) * 0.5;
  const py = 1 - Math.abs(px) - Math.abs(pz);
  const l = Math.hypot(px, py, pz) || 1;
  return [px / l, Math.max(0, py) / l, pz / l];
}

/** The view direction baked into frame (i, j) of an N×N grid (cell centres). */
export function frameDirection(i: number, j: number, frames = IMPOSTOR_FRAMES): [number, number, number] {
  return hemiOctDecode((i + 0.5) / frames, (j + 0.5) / frames);
}

/** The frame (i, j) whose direction is nearest to a view direction. */
export function frameFor(x: number, y: number, z: number, frames = IMPOSTOR_FRAMES): [number, number] {
  const [u, v] = hemiOctEncode(x, Math.max(0, y), z);
  return [Math.min(frames - 1, Math.floor(u * frames)), Math.min(frames - 1, Math.floor(v * frames))];
}

/**
 * THREE-FRAME BLEND (render wave 1, 2026-09-17). The continuous frame
 * coordinate `g = (u, v)·N − ½` puts frame centres on the integer lattice;
 * the grid cell around `g` splits into two triangles along its diagonal and
 * the fragment blends the three frames of the triangle it falls in with the
 * barycentric weights — continuous across every edge, so an orbiting camera
 * never sees a far tree FLIP from one baked view to the next. Frames clamp to
 * the grid (a direction past the last centre keeps that frame's weight).
 * `createImpostorMaterial` evaluates exactly this in TSL.
 */
export function frameTriangle(u: number, v: number, frames = IMPOSTOR_FRAMES): { frames: [[number, number], [number, number], [number, number]]; weights: [number, number, number] } {
  const cl = (x: number): number => Math.max(0, Math.min(frames - 1, x));
  const gx = u * frames - 0.5, gy = v * frames - 0.5;
  const cx = Math.floor(gx), cy = Math.floor(gy);
  const fx = gx - cx, fy = gy - cy;
  if (fx + fy < 1) return { frames: [[cl(cx), cl(cy)], [cl(cx + 1), cl(cy)], [cl(cx), cl(cy + 1)]], weights: [1 - fx - fy, fx, fy] };
  return { frames: [[cl(cx + 1), cl(cy + 1)], [cl(cx + 1), cl(cy)], [cl(cx), cl(cy + 1)]], weights: [fx + fy - 1, 1 - fy, 1 - fx] };
}

export interface ImpostorAtlas {
  texture: THREE.Texture;
  /** Bounding-sphere radius of the source (m) — the quad's half size. */
  radius: number;
  /** World-space centre offset of the source's bounding sphere (m). */
  centerY: number;
  frames: number;
  dispose(): void;
}

/**
 * Bake a source object into an impostor atlas. Renders N×N orthographic views
 * over the upper hemisphere into one render target (RGBA, alpha = coverage).
 * The source keeps its own materials under a flat white light so the atlas
 * carries albedo-like colour; the quad is lit at draw time.
 */
export function bakeImpostorAtlas(renderer: THREE.WebGPURenderer, source: THREE.Object3D, frames = IMPOSTOR_FRAMES, atlasPx = IMPOSTOR_ATLAS_PX): ImpostorAtlas {
  const box = new THREE.Box3().setFromObject(source);
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(0.1, sphere.radius);
  const center = sphere.center.clone();
  const rt = new THREE.RenderTarget(atlasPx, atlasPx, { format: THREE.RGBAFormat, type: THREE.UnsignedByteType, depthBuffer: true, colorSpace: THREE.SRGBColorSpace });
  rt.texture.minFilter = THREE.LinearMipmapLinearFilter;
  rt.texture.magFilter = THREE.LinearFilter;
  rt.texture.generateMipmaps = true;
  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 1.6));
  const sun = new THREE.DirectionalLight(0xffffff, 1.2);
  sun.position.set(0.4, 1, 0.3);
  scene.add(sun);
  const parentBefore = source.parent;
  scene.add(source);
  const cam = new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0.01, radius * 4);
  const tile = atlasPx / frames;
  const prevRT = renderer.getRenderTarget();
  // three r185 hands back a Color4 (the pinned @types/three 0.182 has no such type).
  const prevClear = new THREE.Color() as THREE.Color & { a?: number };
  (renderer as unknown as { getClearColor(t: unknown): void }).getClearColor(prevClear);
  const prevAlpha = prevClear.a ?? 1;
  const prevScissorTest = renderer.getScissorTest();
  renderer.setRenderTarget(rt);
  renderer.setClearColor(0x000000, 0);
  renderer.setScissorTest(true);
  for (let j = 0; j < frames; j++) {
    for (let i = 0; i < frames; i++) {
      const [dx, dy, dz] = frameDirection(i, j, frames);
      cam.position.set(center.x + dx * radius * 2, center.y + dy * radius * 2, center.z + dz * radius * 2);
      cam.up.set(0, 1, 0);
      if (dy > 0.999) cam.up.set(0, 0, -1); // straight down: keep a stable roll
      cam.lookAt(center);
      cam.updateMatrixWorld();
      renderer.setViewport(i * tile, j * tile, tile, tile);
      renderer.setScissor(i * tile, j * tile, tile, tile);
      renderer.clear();
      renderer.render(scene, cam);
    }
  }
  renderer.setScissorTest(prevScissorTest);
  renderer.setClearColor(prevClear, prevAlpha);
  renderer.setRenderTarget(prevRT);
  const size = renderer.getSize(new THREE.Vector2());
  renderer.setViewport(0, 0, size.x, size.y);
  renderer.setScissor(0, 0, size.x, size.y);
  scene.remove(source);
  if (parentBefore) parentBefore.add(source);
  return { texture: rt.texture, radius, centerY: center.y, frames, dispose: () => rt.dispose() };
}

/**
 * The impostor material: a lit, alpha-tested quad whose colour is the atlas
 * frame nearest the fragment's own view direction (hemi-octahedral encode in
 * TSL). The quad is billboarded on the CPU (ImpostorInstancer), so the frame
 * image maps straight onto it.
 */
export function createImpostorMaterial(atlas: ImpostorAtlas, blend = false): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial();
  const frames = float(atlas.frames);
  /** The hemi-octahedral encode of the fragment's own view direction → (u, v) ∈ [0,1]². */
  const encode = (): [ReturnType<typeof float>, ReturnType<typeof float>] => {
    const d = normalize(cameraPosition.sub(positionWorld));
    const dy = clamp(d.y, 0.0, 1.0);
    const l1 = abs(d.x).add(dy).add(abs(d.z)).max(1e-4);
    const px = d.x.div(l1), pz = d.z.div(l1);
    const u = px.add(pz).mul(0.5).add(0.5);
    const v = pz.sub(px).mul(0.5).add(0.5);
    return [u as never, v as never];
  };
  const frameUv = Fn(() => {
    const [u, v] = encode();
    const fi = clamp(floor(u.mul(frames)), 0, frames.sub(1));
    const fj = clamp(floor(v.mul(frames)), 0, frames.sub(1));
    return vec2(fi, fj).add(uv()).div(frames);
  });
  // THREE-FRAME BLEND (render wave 1): the triangle of the grid cell around
  // the continuous frame coordinate, barycentric weights — `frameTriangle` is
  // the CPU mirror the harness pins. Three taps instead of one on the far ring.
  const blended = Fn(() => {
    const [u, v] = encode();
    const g = vec2(u, v).mul(frames).sub(0.5);
    const c = floor(g);
    const f = g.sub(c);
    const lower = f.x.add(f.y).lessThan(1.0);
    const f0 = select(lower, c, c.add(1.0));
    const f1 = c.add(vec2(1.0, 0.0));
    const f2 = c.add(vec2(0.0, 1.0));
    const w0 = select(lower, float(1).sub(f.x).sub(f.y), f.x.add(f.y).sub(1.0));
    const w1 = select(lower, f.x, float(1).sub(f.y));
    const w2 = select(lower, f.y, float(1).sub(f.x));
    const at = (fr: unknown) => tslTexture(atlas.texture, clamp(fr as never, 0.0, frames.sub(1)).add(uv()).div(frames) as never);
    return at(f0).mul(w0).add(at(f1).mul(w1)).add(at(f2).mul(w2));
  });
  const sample = blend ? blended() : tslTexture(atlas.texture, frameUv() as never);
  m.colorNode = sample.rgb;
  m.opacityNode = sample.a;
  m.alphaTest = 0.45;
  m.transparent = false;
  m.roughness = 0.9;
  m.metalness = 0;
  m.side = THREE.DoubleSide;
  // Motion vectors (2026-09-12): the quads are re-aimed on the CPU and their
  // slots reused as trees cross the ring — three's frozen previous instance
  // matrices streaked every impostor on a still camera. Static pin.
  pinStaticMotionVectors(m);
  // Keep `sign`/`select`/`vec3` referenced for the TSL bundle (tree-shaken otherwise).
  void sign; void select; void vec3;
  return m;
}

/**
 * Per-species instanced billboards under the render anchor (float32 instance
 * matrices ride the floating origin like every other instancer). Quads face
 * the camera; the yaw refresh runs every REFACE_EVERY frames — far trees turn
 * slowly on screen.
 */
export class ImpostorInstancer {
  readonly mesh: THREE.InstancedMesh;
  private readonly abs = new Map<number, { x: number; y: number; z: number; s: number }>();
  private readonly slots: number[] = [];
  private readonly free: number[] = [];
  private nextId = 1;
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly offAnchor: () => void;
  private readonly tmpM = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpP = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();
  private readonly tmpAbs = new THREE.Matrix4();
  private frame = 0;
  static REFACE_EVERY = 12;

  constructor(private readonly scene: THREE.Scene, readonly atlas: ImpostorAtlas, capacity = 4096, label = 'impostor', blend = false) {
    const geo = new THREE.PlaneGeometry(atlas.radius * 2, atlas.radius * 2);
    this.mesh = new THREE.InstancedMesh(geo, createImpostorMaterial(atlas, blend), capacity);
    this.mesh.name = `impostors:${label}`;
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false; // the far ring never cast (see the shadow caster rule)
    this.mesh.receiveShadow = false;
    this.anchor = makeAnchorGroup(`anchor:${label}`);
    this.anchor.group.add(this.mesh);
    scene.add(this.anchor.group);
    this.offAnchor = onRenderAnchor(() => this.reface(true));
  }

  /** Add a tree at world (x, y, z) with uniform scale s; returns a handle (or -1 when full). */
  add(x: number, y: number, z: number, s: number): number {
    let slot: number;
    if (this.free.length) slot = this.free.pop() as number;
    else { if (this.mesh.count >= (this.mesh.instanceMatrix.count)) return -1; slot = this.mesh.count++; }
    const id = this.nextId++;
    this.slots[slot] = id;
    this.abs.set(id, { x, y: y + this.atlas.centerY * s, z, s });
    this.writeSlot(slot, id, null);
    return id;
  }

  remove(id: number): void {
    const slot = this.slots.indexOf(id);
    if (slot < 0) return;
    this.abs.delete(id);
    // Swap-remove keeps `count` dense.
    const last = this.mesh.count - 1;
    if (slot !== last) {
      const lastId = this.slots[last];
      this.slots[slot] = lastId;
      this.writeSlot(slot, lastId, null);
    }
    this.slots.length = last;
    this.mesh.count = last;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  get instanceCount(): number { return this.abs.size; }

  private writeSlot(slot: number, id: number, camPos: THREE.Vector3 | null): void {
    const a = this.abs.get(id);
    if (!a) return;
    this.tmpP.set(a.x, a.y, a.z);
    if (camPos) {
      // Full billboard: face the camera (the baked frame was rendered from that direction).
      this.tmpM.lookAt(camPos, this.tmpP, THREE.Object3D.DEFAULT_UP);
      this.tmpQ.setFromRotationMatrix(this.tmpM);
    } else this.tmpQ.identity();
    this.tmpS.set(a.s, a.s, a.s);
    this.tmpAbs.compose(this.tmpP, this.tmpQ, this.tmpS);
    this.mesh.setMatrixAt(slot, toAnchorLocal(this.tmpAbs, this.tmpM));
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Re-aim every quad at the camera (every REFACE_EVERY frames, or now when forced). */
  reface(force = false, camPos?: THREE.Vector3): void {
    if (!force && (++this.frame % ImpostorInstancer.REFACE_EVERY) !== 0) return;
    const cp = camPos ?? this.lastCam;
    if (!cp) return;
    this.lastCam = cp.clone();
    for (let slot = 0; slot < this.mesh.count; slot++) this.writeSlot(slot, this.slots[slot], cp);
  }
  private lastCam: THREE.Vector3 | null = null;

  dispose(): void {
    this.offAnchor();
    this.anchor.release();
    this.scene.remove(this.anchor.group);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
    this.abs.clear();
  }
}
